import { fetchBulkOrderByQuery } from '@/api';
import { ALLOW_FILE_CSV } from '@/config';
import { message, Upload } from 'antd';
import { RcFile } from 'antd/es/upload';
import { t } from 'i18next';
import { handleErrorMessage } from '../handleError';

/**
 * Checks if a file has any validation errors for upload.
 * This function is designed to work with Ant Design's Upload component.
 *
 * @param {RcFile} file - The file to check for validation errors. Must be an instance of Ant Design's RcFile.
 *
 * @returns {string | false}
 * - Returns `false` if no validation errors are found (file is valid)
 * - Returns `Upload.LIST_IGNORE` if validation errors are found (file is invalid)
 *
 * @throws {void} This function doesn't throw errors, but displays error messages using Ant Design's message component
 */
export const checkFileValidationError = (file: RcFile) => {
  const isCSVOrXLSX = ALLOW_FILE_CSV.includes(file.type);
  const isFileSizeValid = file.size <= 1048576;

  if (!isCSVOrXLSX) {
    message.error(t('only_csv_xlsx'));
    return Upload.LIST_IGNORE;
  }
  if (!isFileSizeValid) {
    message.error(t('file_size_limit_exceeded'));
    return Upload.LIST_IGNORE;
  }

  return false;
};

export const downloadFile = async (
  uid: string,
  fetchFunction: (uid: string) => Promise<Blob>,
  fileName: string,
) => {
  try {
    const fileBlob = await fetchFunction(uid);

    const url = window.URL.createObjectURL(fileBlob);

    triggerDownload(url, fileName);

    return true;
  } catch (_error: any) {
    const error = await convertBlobErrorToJSON(_error);

    handleErrorMessage(error, t('operation_failed_please_try_again'), true);

    return false;
  }
};

export const triggerDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  // link.setAttribute('target', '_blank');

  document.body.appendChild(link);
  link.click();

  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const convertBlobErrorToJSON = async (error: any) => {
  if (error.response && error.response.data instanceof Blob) {
    const errorText = await error.response.data.text();
    const errorJSON = JSON.parse(errorText);

    return { ...errorJSON, statusCode: error.status };
  } else {
    console.error('Error:', error);
    return null;
  }
};

export const exportOrders = async (queryString: string, fileName?: string) => {
  return await downloadFile(
    queryString,
    fetchBulkOrderByQuery,
    `orders-${new Date().getTime()}.csv`, // Timestamp only for unique filename, not data
  );
};
